import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Tab, Tabs, Typography } from '@material-ui/core';
import { Box, Icon, Loader } from '@saviynt/design-system';

import { setActiveTab, setMigratedConfigObjects, clearAllData } from '../../redux/migrationSlice';
import { getSourceConfig } from '../../utils/migrationUtils';
import {
  fetchSailpointConnection,
  fetchOIMConnection,
  fetchSourceStatus,
} from '../../redux/migrationThunks';
import { TITLES, UI_LABELS } from '../../constants/stringConstants';
import useStyles from './styles/MigrationWorkbench.styles';

import GettingStarted from '../GettingStarted';
import Visibility from '../Visibility';
import ConfigurationMigration from '../ConfigurationMigration';
import DataMigration from '../DataMigration';
import styles from '../../styles/MigrationWorkbench.module.css';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && children}</div>
);

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number,
  index: PropTypes.number,
};

TabPanel.defaultProps = {
  children: null,
  value: 0,
  index: 0,
};

const reverseTabMap = {
  0: 'getting-started',
  1: 'visibility',
  2: 'configuration',
  3: 'data-migration',
};

const MigrationWorkbench = () => {
  const classes = useStyles();
  const { sourceType, tab } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { sourceStatus } = useSelector((state) => state.migration);
  const discoveryCompleted = !!sourceStatus?.discoveryCompleted;
  const dataMigrationEnabled = !!sourceStatus?.dataMigrationEnabled;
  const dataMigrationTabEnabled = discoveryCompleted && dataMigrationEnabled;

  const tabMap = { 'getting-started': 0, visibility: 1, configuration: 2, 'data-migration': 3 };
  const currentTab = tabMap[tab] || 0;
  const config = getSourceConfig(sourceType);
  const [sourceID, setSourceID] = useState();
  const [connectionName, setConnectionName] = useState();

  const handleConnectionSaved = useCallback(
    async (savedConnectionName) => {
      setConnectionName(savedConnectionName);
      try {
        const thunk = sourceType?.includes('oim') ? fetchOIMConnection : fetchSailpointConnection;
        const connResponse = await dispatch(thunk()).unwrap();
        const newSourceId = connResponse?.data?.sourceId;
        setSourceID(newSourceId ?? null);
        if (connResponse?.data?.connectionName) {
          setConnectionName(connResponse.data.connectionName);
        }
      } catch (error) {
        /* silenced */
      }
    },
    [sourceType, dispatch]
  );

  useEffect(() => {
    let isMounted = true;
    dispatch(clearAllData());
    setSourceID(undefined);
    setConnectionName(undefined);

    const init = async () => {
      try {
        const thunk = sourceType?.includes('oim') ? fetchOIMConnection : fetchSailpointConnection;
        const connResponse = await dispatch(thunk()).unwrap();
        const sourceId = connResponse?.data?.sourceId;

        if (isMounted) {
          setSourceID(sourceId ?? null);
          setConnectionName(connResponse?.data?.connectionName);
        }

        if (sourceId && isMounted) {
          dispatch(fetchSourceStatus(sourceId));
        }
      } catch (error) {
        if (isMounted) {
          setSourceID(null);
        }
      }
    };

    init().catch(() => { /* silenced */ });

    return () => {
      isMounted = false;
    };
  }, [sourceType, dispatch]);

  useEffect(() => {
    document.title = sourceType?.includes('oim')
      ? TITLES.OIM_MIGRATION
      : TITLES.SAILPOINT_MIGRATION;
    return () => {
      document.title = UI_LABELS.SAVIYNT_TITLE;
    };
  }, [sourceType]);

  const handleTabChange = useCallback(
    (event, newValue) => {
      history.push(`/migration/${sourceType}/${reverseTabMap[newValue]}`);
      dispatch(setActiveTab(newValue));
    },
    [dispatch, history, sourceType]
  );

  const handleNavigate = useCallback(
    (tabIndex) => {
      history.push(`/migration/${sourceType}/${reverseTabMap[tabIndex]}`);
      dispatch(setActiveTab(tabIndex));
    },
    [dispatch, history, sourceType]
  );

  const handleMigrationUpdate = useCallback(
    (migrated) => {
      dispatch(setMigratedConfigObjects(migrated));
    },
    [dispatch]
  );

  const renderSourceStatus = () => {
    if (sourceID === undefined) {
      return <Loader kind="dots" format="inline" color="brand" />;
    }
    if (sourceID) {
      return (
        <span className={classes.statusConnected}>
          <span className={classes.statusDot} />
          Source: {connectionName}
        </span>
      );
    }
    return (
      <span className={classes.statusNotConfigured}>
        <span className={classes.statusDotError} />
        Not Configured
        <span className={classes.statusHint}>→ Configure in Prerequisites Settings</span>
      </span>
    );
  };

  if (!config) {
    return (
      <div className={classes.errorContainer}>
        <Typography variant="h5">Source type not supported</Typography>
      </div>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.content}>
        <Box className={styles.header}>
          <Box className={styles.breadcrumb}>
            <Box className={styles.appIcon}>
              {sourceType?.includes('oim') ? (
                <img src="/ECMv6/assets/images/oracle.png" alt="oracle" />
              ) : (
                <img src="/ECMv6/assets/images/sailpoint.png" height="31" width="38" alt="sailpoint" />
              )}
            </Box>
            <Box className={styles.appInfo}>
              <Typography variant="h4" component="h1">
                {config.name} Migration Workbench
              </Typography>
              <Typography variant="body2" className={styles.subtitle}>
                {renderSourceStatus()}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className={styles.tabs}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            className={classes.tabsContainer}
            TabIndicatorProps={{ className: classes.tabIndicator }}
          >
            <Tab
              label={
                <span className={classes.tabLabel}>
                  <Icon kind="Applications" label="Applications" size="medium" />
                  Getting Started
                </span>
              }
              className={`${classes.tab} ${
                currentTab === 0 ? classes.tabActive : classes.tabInactive
              }`}
            />
            <Tab
              label={
                <span className={classes.tabLabel}>
                  <Icon kind="Dashboard" label="Dashboard" size="medium" />
                  Visibility
                </span>
              }
              disabled={!discoveryCompleted}
              className={`${classes.tab} ${
                !discoveryCompleted
                  ? classes.tabDisabled
                  : classes[currentTab === 1 ? 'tabActive' : 'tabInactive']
              }`}
            />
            <Tab
              label={
                <span className={classes.tabLabel}>
                  <Icon kind="Configure" label="Configure" size="medium" />
                  Configuration Migration
                </span>
              }
              disabled={!discoveryCompleted}
              className={`${classes.tab} ${
                !discoveryCompleted
                  ? classes.tabDisabled
                  : classes[currentTab === 2 ? 'tabActive' : 'tabInactive']
              }`}
            />
            <Tab
              label={
                <span className={classes.tabLabel}>
                  <Icon kind="QuickAction" label="QuickAction" size="medium" />
                  Data Migration
                </span>
              }
              disabled={!dataMigrationTabEnabled}
              className={`${classes.tab} ${
                !dataMigrationTabEnabled
                  ? classes.tabDisabled
                  : classes[currentTab === 3 ? 'tabActive' : 'tabInactive']
              }`}
            />
          </Tabs>
        </Box>

        <div className={styles.tabContent}>
          <TabPanel value={currentTab} index={0}>
            <GettingStarted
              sourceID={sourceID}
              sourceType={sourceType}
              onNavigate={handleNavigate}
              onConnectionSaved={handleConnectionSaved}
              dataMigrationTabEnabled={dataMigrationTabEnabled}
            />
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <Visibility sourceID={sourceID} sourceType={sourceType} />
          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            <ConfigurationMigration
              sourceID={sourceID}
              sourceType={sourceType}
              onMigrationUpdate={handleMigrationUpdate}
            />
          </TabPanel>
          <TabPanel value={currentTab} index={3}>
            <DataMigration sourceID={sourceID} sourceType={sourceType} />
          </TabPanel>
        </div>
      </Box>
    </Box>
  );
};

export default MigrationWorkbench;
